'use client';

/**
 * SplitPanelContent Component
 *
 * Scrollable content area for SplitPanel.
 */

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import React from 'react';
import { useReducedMotion } from '../../hooks';
import { durations } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export interface SplitPanelContentProps {
  /** Content to display */
  children: React.ReactNode;
  /** Padding size */
  padding?: number;
  /** Enable content animation */
  animate?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

// Motion-enabled Box for animations
const MotionBox = motion.create(Box);

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0, // Required for nested flex containers to properly calculate height
  overflowY: 'auto',
  overflowX: 'hidden',

  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
    borderRadius: 3,

    '&:hover': {
      background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)',
    },
  },
}));

// Remove top padding from the first section header so content connects flush
// to SplitPanelHeader's bottom border. Scoped to the first direct child so
// subsequent sections keep their spacing.
const flushFirstSectionHeaderStyles = {
  '& > *:first-of-type [data-split-panel-section-header]': {
    paddingTop: 0,
  },
} as const;

// ============================================================================
// Animation Variants
// ============================================================================

// Bezier easing curve (typed as tuple for framer-motion compatibility)
const easeOutCubic: [number, number, number, number] = [0.215, 0.61, 0.355, 1];

const contentVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: durations.normal / 1000,
      ease: easeOutCubic,
      delay: 0.1,
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export const SplitPanelContent: React.FC<SplitPanelContentProps> = ({ children, padding = 2.5, animate = true }) => {
  const reducedMotion = useReducedMotion();
  const shouldAnimate = animate && !reducedMotion;

  // Symmetric padding on all four sides so body content gets breathing room
  // under SplitPanelHeader. The flush-first-section override below still kicks
  // in for nested Section components (whose internal title padding would
  // otherwise stack with this body padding); for non-Section first children
  // (cards, lists, plain Boxes) the symmetric padding is what's wanted.
  const innerSx = { p: padding, ...flushFirstSectionHeaderStyles };

  return (
    <ContentContainer>
      {shouldAnimate ? (
        <MotionBox sx={innerSx} variants={contentVariants} initial="hidden" animate="visible">
          {children}
        </MotionBox>
      ) : (
        <Box sx={innerSx}>{children}</Box>
      )}
    </ContentContainer>
  );
};

export default SplitPanelContent;
